import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";
import React, { useEffect, useState } from "react";
import { getItem } from "../../../../../utils/localStorage";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import {
  getCustomersURL,
  getUnitsForFacility,
  get_leases
} from "../../../../../utils/urls";

const BasicInformationTab = ({
  formData,
  updateFormData,
  onNext,
  validation,
  isEdit = false,
  currentLeaseData = null
}) => {
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [landlords, setLandlords] = useState([]);
  // Units left out of the list, with the reason, so it is clear why a unit is missing.
  const [hiddenUnits, setHiddenUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        setLoading(false);
        return;
      }

      const [unitsRes, customersRes, leasesRes] = await Promise.all([
        makeRequest2(`${getUnitsForFacility}/${facilityId}`, "GET"),
        makeRequest2(`${getCustomersURL}/${facilityId}`, "GET"),
        makeRequest2(`${get_leases}/${facilityId}`, "GET")
      ]);

      // Handle Customers Response first (needed for unit filtering)
      let allCustomers = [];
      if (customersRes && customersRes.success) {
        if (Array.isArray(customersRes.data)) {
          allCustomers = customersRes.data;
        } else if (customersRes.data && customersRes.data.customers) {
          allCustomers = customersRes.data.customers;
        } else if (customersRes.data && customersRes.data.data) {
          allCustomers = customersRes.data.data;
        }
      } else {
        console.log("Failed to load customers");
      }

      // Handle Units Response
      if (unitsRes && unitsRes.success) {
        console.log("Full units response:", unitsRes);
        let unitsData = [];
        let landlordsData = [];

        if (Array.isArray(unitsRes.data)) {
          unitsData = unitsRes.data;
        } else if (unitsRes.data && unitsRes.data.units) {
          unitsData = unitsRes.data.units;
        } else if (unitsRes.data && unitsRes.data.data) {
          unitsData = unitsRes.data.data;
        }

        // Check for landlords in various possible locations
        if (unitsRes.data && unitsRes.data.landlords) {
          landlordsData = unitsRes.data.landlords;
        } else if (unitsRes.data && unitsRes.data.data && unitsRes.data.data.landlords) {
          landlordsData = unitsRes.data.data.landlords;
        }

        console.log("Processed units data:", unitsData);
        console.log("Processed landlords data:", landlordsData);

        // Get existing lease unit IDs to exclude
        let existingLeaseUnits = [];
        if (leasesRes && leasesRes.success) {
          console.log("Full leases response:", leasesRes);
          let leasesData = [];

          if (Array.isArray(leasesRes.data)) {
            leasesData = leasesRes.data;
          } else if (leasesRes.data && leasesRes.data.leaseAgreements) {
            leasesData = leasesRes.data.leaseAgreements;
          } else if (leasesRes.data && leasesRes.data.leases) {
            leasesData = leasesRes.data.leases;
          } else if (leasesRes.data && leasesRes.data.data) {
            leasesData = leasesRes.data.data;
          }

          console.log("Processed leases data:", leasesData);
          console.log("Is leasesData an array?", Array.isArray(leasesData));

          // Only filter if leasesData is actually an array
          if (Array.isArray(leasesData)) {
            // Filter for any lease that is NOT terminated (exclude units with active, pending, expired, or any other status except terminated)
            const activeLeases = leasesData.filter(lease => {
              if (!lease.status) return false;
              const status = lease.status.toLowerCase();
              // Only exclude units with Active, Pending, Expired leases - allow units with Terminated leases or no leases
              return ['active', 'pending', 'expired'].includes(status);
            });

            // Create a comprehensive list of unit identifiers to exclude
            existingLeaseUnits = [];
            activeLeases.forEach(lease => {
              // Add both the ObjectId and string representations
              if (lease.unitNumber) {
                if (typeof lease.unitNumber === 'object' && lease.unitNumber._id) {
                  existingLeaseUnits.push(lease.unitNumber._id.toString());
                } else {
                  existingLeaseUnits.push(lease.unitNumber.toString());
                }
              }
              if (lease.unitId) {
                existingLeaseUnits.push(lease.unitId.toString());
              }
            });

            console.log("Units with existing non-terminated leases:", existingLeaseUnits);
            console.log("Lease statuses found:", leasesData.map(lease => ({
              unit: lease.unitNumber || lease.unitId,
              status: lease.status,
              unitNumberType: typeof lease.unitNumber,
              unitIdType: typeof lease.unitId
            })));
          } else {
            console.log("Leases data is not an array, skipping lease filtering");
          }
        } else {
          console.log("Failed to fetch leases or no leases data available");
        }

        // Filter units based on tenant/landlord assignment and lease status
        const hidden = [];
        const filteredUnits = unitsData.filter(unit => {
          // Must have a home owner (landlord)
          if (!unit.homeOwnerId) {
            console.log(`Unit ${unit.name}: Excluded - No homeOwnerId`);
            hidden.push({ name: unit.name, reason: "No landlord (home owner) is assigned to the unit" });
            return false;
          }

          // Check if home owner is also a resident (can be both landlord and tenant)
          let hasValidTenantAssignment = false;

          if (unit.tenantId) {
            // Has explicit tenant assignment
            hasValidTenantAssignment = true;
          } else if (unit.homeOwnerId) {
            // Check if the home owner is a resident (this would make them both landlord and tenant)
            const homeOwner = allCustomers.find(customer =>
              customer._id && customer._id.toString() === unit.homeOwnerId.toString()
            );

            if (homeOwner && homeOwner.residentType && homeOwner.residentType.toLowerCase() === 'resident') {
              hasValidTenantAssignment = true;
              console.log(`Unit ${unit.name}: Home owner ${homeOwner.firstName} ${homeOwner.lastName} is a resident, can serve as both landlord and tenant`);
            }
          }

          if (!hasValidTenantAssignment) {
            console.log(`Unit ${unit.name}: Excluded - No valid tenant assignment (no tenantId and homeOwner is not resident)`);
            hidden.push({ name: unit.name, reason: "No tenant is assigned to the unit (assign the new tenant to the unit first)" });
            return false;
          }

          // In edit mode, allow the current unit even if it has an existing lease
          const isCurrentEditUnit = isEdit && currentLeaseData &&
            (unit._id === formData.basicInfo.unitNumber || unit.name === formData.basicInfo.unitName);

          // Check against unit._id in all its forms
          const unitIdString = unit._id ? unit._id.toString() : '';
          const doesNotHaveExistingLease = !existingLeaseUnits.includes(unitIdString) &&
                                          !existingLeaseUnits.includes(unit._id) &&
                                          !existingLeaseUnits.includes(unit.name);

          if (!doesNotHaveExistingLease && !isCurrentEditUnit) {
            console.log(`Unit ${unit.name}: Excluded - Has existing active/pending lease`);
            hidden.push({ name: unit.name, reason: "Already has an active, pending or expired lease (terminate or renew it first)" });
            return false;
          }

          console.log(`Unit ${unit.name} (${unit._id}): INCLUDED - hasValidTenantAssignment=${hasValidTenantAssignment}, doesNotHaveExistingLease=${doesNotHaveExistingLease}, isCurrentEditUnit=${isCurrentEditUnit}`);

          return true;
        });

        console.log("Filtered units (with tenant, landlord, no existing lease):", filteredUnits);

        setUnits(filteredUnits);
        setHiddenUnits(hidden);
        setLandlords(landlordsData);
      } else {
        toastify("Failed to load units", "error");
      }

      // Process customers for tenant/landlord dropdowns
      const tenantCustomers = allCustomers.filter(
        customer => customer.customerType && customer.customerType.toLowerCase() === "tenant"
      );

      const landlordCustomers = allCustomers.filter(
        customer => customer.customerType && customer.customerType.toLowerCase() === "home owner"
      );

      // Also include customers who are both tenant and home owner in both arrays
      const dualRoleCustomers = allCustomers.filter(
        customer => customer.customerType &&
        (customer.customerType.toLowerCase().includes("tenant") && customer.customerType.toLowerCase().includes("home owner"))
      );

      // Combine arrays with dual role customers
      const allTenants = [...tenantCustomers, ...dualRoleCustomers];
      const allLandlords = [...landlordCustomers, ...dualRoleCustomers];

      console.log("Filtered tenants:", allTenants);
      console.log("Filtered landlords:", allLandlords);
      console.log("Dual role customers:", dualRoleCustomers);

      setTenants(allTenants);
      setLandlords(allLandlords);


    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toastify("Failed to load form data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUnitChange = (unitId) => {
    const selectedUnit = units.find(unit => unit._id === unitId);

    if (selectedUnit) {
      // Initialize update object
      let updates = {
        unitNumber: unitId,
        unitName: selectedUnit.name,
        unitType: selectedUnit.unitType, // Add unitType here
        // Clear previous selections
        landlordId: "",
        landlordName: "",
        tenant: "",
        tenantName: ""
      };

      // Use homeOwnerId as the landlord
      const landlordId = selectedUnit.homeOwnerId;
      if (landlordId) {
        const landlord = landlords.find(l => l._id === landlordId);
        console.log("Found landlord:", landlord);
        if (landlord) {
          updates.landlordId = landlordId;
          updates.landlordName = `${landlord.firstName} ${landlord.lastName}`;
        } else {
          console.log("Landlord ID found but landlord not in landlords array:", landlordId);
        }
      } else {
        console.log("No homeOwnerId found on unit");
      }

      // Handle tenant assignment
      const tenantId = selectedUnit.tenantId;
      if (tenantId) {
        // Explicit tenant assignment
        const tenant = tenants.find(t => t._id === tenantId);
        if (tenant) {
          updates.tenant = tenantId;
          updates.tenantName = `${tenant.firstName} ${tenant.lastName}`;

          // Check if this is a dual-role customer (same person is both tenant and landlord)
          if (tenantId === landlordId) {
            console.log("Same customer is both tenant and landlord");
            updates.landlordId = tenantId;
            updates.landlordName = `${tenant.firstName} ${tenant.lastName}`;
          }
        } else {
          console.log("Tenant ID found but tenant not in tenants array:", tenantId);
        }
      } else if (landlordId) {
        // No explicit tenant, check if home owner is a resident (can serve as both)
        const homeOwner = landlords.find(l => l._id === landlordId);
        if (homeOwner && homeOwner.residentType && homeOwner.residentType.toLowerCase() === 'resident') {
          console.log("Home owner is a resident, using same ID for both tenant and landlord");
          updates.tenant = landlordId;
          updates.tenantName = `${homeOwner.firstName} ${homeOwner.lastName}`;
          // Landlord details already set above
        } else {
          console.log("No tenantId found and homeOwner is not a resident");
        }
      } else {
        console.log("No tenantId found on unit and no landlordId available");
      }

      console.log("Final updates:", updates);
      // Apply all updates at once
      updateFormData.updateBasicInfo(updates);
    }
  };

  const handleNext = () => {
    const validationResult = validation.validateBasicInfo();

    if (!validationResult.isValid) {
      validationResult.errors.forEach(error => {
        toastify(error, "error");
      });
      return;
    }

    onNext();
  };

  if (loading) {
    return (
      <div className="surface-card p-6 border-round-lg shadow-2">
        <div className="flex align-items-center mb-4">
          <i className="ti ti-info-circle text-2xl text-primary mr-3"></i>
          <h3 className="text-xl font-semibold text-900 m-0">Basic Information</h3>
        </div>
        <div className="grid">
          <div className="col-12 md:col-6 mb-4">
            <div className="field">
              <label className="block text-900 font-medium mb-2">Unit</label>
              <Skeleton width="100%" height="3rem"></Skeleton>
            </div>
          </div>
          <div className="col-12 md:col-6 mb-4">
            <div className="field">
              <label className="block text-900 font-medium mb-2">Tenant</label>
              <Skeleton width="100%" height="3rem"></Skeleton>
            </div>
          </div>
          <div className="col-12 md:col-6 mb-4">
            <div className="field">
              <label className="block text-900 font-medium mb-2">Landlord</label>
              <Skeleton width="100%" height="3rem"></Skeleton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="row">
        {/* Unit Selection */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label htmlFor="unit">
              Unit <span className="text-danger">*</span>
            </label>
            <Dropdown
              id="unit"
              value={formData.basicInfo.unitNumber}
              options={units}
              optionLabel="name"
              optionValue="_id"
              onChange={(e) => handleUnitChange(e.value)}
              placeholder="Select a unit"
              className="w-100"
              filter
              showClear
            />
            {hiddenUnits.length > 0 && (
              <details className="mt-2">
                <summary className="text-muted" style={{ cursor: "pointer", fontSize: "0.85rem" }}>
                  {hiddenUnits.length} unit{hiddenUnits.length > 1 ? "s are" : " is"} not available for a new lease - why?
                </summary>
                <ul className="small text-muted mb-0 mt-1" style={{ maxHeight: "9rem", overflowY: "auto" }}>
                  {hiddenUnits.map((hiddenUnit) => (
                    <li key={hiddenUnit.name}>
                      <strong>{hiddenUnit.name}</strong>: {hiddenUnit.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>

        {/* Unit Type Display */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label>Unit Type</label>
            <div className="form-control bg-light d-flex align-items-center" style={{ height: "42px" }}>
              {formData.basicInfo.unitType ? (
                <span>{formData.basicInfo.unitType}</span>
              ) : (
                <span className="text-muted">Will be auto-filled when unit is selected</span>
              )}
            </div>
          </div>
        </div>

        {/* Tenant Information (Auto-filled) */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label>Tenant</label>
            <div className="form-control bg-light d-flex align-items-center" style={{ height: "42px" }}>
              {formData.basicInfo.tenantName ? (
                <span>{formData.basicInfo.tenantName}</span>
              ) : (
                <span className="text-muted">Will be auto-filled when unit is selected</span>
              )}
            </div>
          </div>
        </div>

        {/* Landlord Information (Auto-filled) */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label>Landlord</label>
            <div className="form-control bg-light d-flex align-items-center" style={{ height: "42px" }}>
              {formData.basicInfo.landlordName ? (
                <span>{formData.basicInfo.landlordName}</span>
              ) : (
                <span className="text-muted">Will be auto-filled when unit is selected</span>
              )}
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <Dropdown
              id="status"
              value={formData.basicInfo.status}
              options={[
                { label: "Pending", value: "Pending" },
                { label: "Active", value: "Active" },
                { label: "Draft", value: "Draft" }
              ]}
              onChange={(e) => updateFormData.updateBasicInfo({ status: e.value })}
              placeholder="Select status"
              className="w-100"
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="col-12 mt-4">
          <div className="d-flex justify-content-end">
            <Button
              label="Next: Lease Terms"
              icon="pi pi-arrow-right"
              iconPos="right"
              onClick={handleNext}
              disabled={!formData.basicInfo.unitNumber || !formData.basicInfo.tenant || !formData.basicInfo.landlordId}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default BasicInformationTab;